import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const Row = ({ id, label, result }) => (
  <li className="flex items-start gap-2" data-testid={`onedrive-check-${id}`} data-ok={result.ok ? "true" : "false"}>
    {result.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" /> : <XCircle size={14} className="mt-0.5 shrink-0 text-red-600" />}
    <span><span className="font-medium text-zinc-800">{label}:</span> {result.message}</span>
  </li>
);

export const ConnectionCheck = ({ autoRun = false, onResult }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const run = useCallback(async () => {
    setBusy(true); setErr(""); setRes(null);
    try {
      const { data } = await api.get("/onedrive/check");
      setRes(data); onResult?.(data);
    } catch (e) {
      setErr(formatApiError(e)); onResult?.({ ok: false, error: formatApiError(e) });
    } finally { setBusy(false); }
  }, [onResult]);

  useEffect(() => { if (autoRun) run(); }, [autoRun, run]);

  return (
    <div className="mt-3 border-t border-zinc-200 pt-3">
      <button type="button" onClick={run} disabled={busy} data-testid="onedrive-check-button"
        className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-xs text-zinc-800 transition-colors hover:bg-zinc-100 disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}{busy ? "Testing Microsoft app settings…" : res || err ? "Test again" : "Test Microsoft app settings"}
      </button>
      {err && <p className="mt-2 text-red-600" data-testid="onedrive-check-error">{err}</p>}
      {res && (
        <div className="mt-3" data-testid="onedrive-check-result" data-ok={res.ok ? "true" : "false"}>
          {res.error ? <p className="text-red-600">{res.error}</p> : (
            <ul className="space-y-1.5">
              <Row id="credentials" label="Client ID & secret" result={res.credentials} />
              <Row id="redirect" label="Redirect URI" result={res.redirect} />
            </ul>
          )}
          <p className={`mt-2 ${res.ok ? "text-emerald-700" : "text-amber-800"}`} data-testid="onedrive-check-summary">
            {res.ok ? "Everything looks right — you can connect OneDrive." : `Fix the items above in Azure Portal — in the app registration whose Application (client) ID starts with ${res.client_id_hint} — then test again before signing in.`}
          </p>
        </div>
      )}
    </div>
  );
};
